import { Routes } from '@angular/router';
import { inject } from '@angular/core';

import { AuthGuard, redirectLoggedInTo, redirectUnauthorizedTo } from 'src/app/guards/auth/auth.guard';
import { ObjectStoreCanDeactivateGuard } from 'src/app/guards/object-store/object-store-can-deactivate.guard';

import { requireProductAccess } from 'src/app/guards/product-access/product-access.guard';
import { PipelineCanDeactivateGuard } from 'src/app/guards/pipeline/pipeline-can-deactivate.guard';
import { RoleGuard } from 'src/app/guards/role/role.guard';

import { ObjectStoragePage } from 'src/app/pages/object-storage/object-storage.page';
import { EditorPage } from 'src/app/pages/editor/editor.page';

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);
const redirectLoggedInToHome = () => redirectLoggedInTo(['workflow']);
const authAndRole = [AuthGuard, RoleGuard];
const aiStudio = [...authAndRole, requireProductAccess('ai-studio')];

export const routes: Routes = [
  {
    path: 'overview',
    loadComponent: () => import('./pages/home/home.page').then((m) => m.HomePage),
    canActivate: authAndRole, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'cluster/:id',
    loadComponent: () => import('./pages/home/home.page').then( m => m.HomePage),
    canActivate: authAndRole, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  // AI Studio pages
  {
    path: 'workflow',
    loadComponent: () => import('./pages/workflow/workflow.page').then( m => m.WorkflowPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'workflow/:id',
    loadComponent: () => import('./pages/workflow/workflow.page').then( m => m.WorkflowPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'agent',
    loadComponent: () => import('./pages/agent/agent.page').then( m => m.AgentPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'agent/:id',
    loadComponent: () => import('./pages/agent/agent.page').then( m => m.AgentPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'data-collection',
    loadComponent: () => import('./pages/data-collection/data-collection.page').then( m => m.DataCollectionPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'data-collection/:id',
    loadComponent: () => import('./pages/data-collection/data-collection.page').then( m => m.DataCollectionPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'mcp-servers',
    loadComponent: () => import('./pages/mcp-servers/mcp-servers.page').then( m => m.MCPServersPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'mcp-servers/:id',
    loadComponent: () => import('./pages/mcp-servers/mcp-servers.page').then( m => m.MCPServersPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'connectors',
    loadComponent: () => import('./pages/connectors/connectors.page').then( m => m.ConnectorsPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'skill',
    loadComponent: () => import('./pages/skill/skill.page').then( m => m.SkillPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'skill/:id',
    loadComponent: () => import('./pages/skill/skill.page').then( m => m.SkillPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'grammar',
    loadComponent: () => import('./pages/grammar/grammar.page').then( m => m.GrammarPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'grammar/:id',
    loadComponent: () => import('./pages/grammar/grammar.page').then( m => m.GrammarPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'model',
    loadComponent: () => import('./pages/model/model.page').then( m => m.ModelPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'editor/:mode/:id',
    loadComponent: () => import('./pages/editor/editor.page').then( m => m.EditorPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin },
    canDeactivate: [(component: EditorPage) => inject(PipelineCanDeactivateGuard).canDeactivate(component)]
  },
  {
    path: 'editor/:mode/:id/:version',
    loadComponent: () => import('./pages/editor/editor.page').then( m => m.EditorPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin },
    canDeactivate: [(component: EditorPage) => inject(PipelineCanDeactivateGuard).canDeactivate(component)]
  },
  {
    path: 'log-explorer/:workflowId',
    loadComponent: () => import('./pages/log-explorer/log-explorer.page').then( m => m.LogExplorerPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  // AI Studio - infrastructure pages
  {
    path: 'pubsub',
    loadComponent: () => import('./pages/pubsub/pubsub.page').then( m => m.PubsubPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'pubsub/:id',
    loadComponent: () => import('./pages/pubsub/pubsub.page').then( m => m.PubsubPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'object-storage',
    loadComponent: () => import('./pages/object-storage/object-storage.page').then( m => m.ObjectStoragePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'object-storage/:id',
    loadComponent: () => import('./pages/object-storage/object-storage.page').then( m => m.ObjectStoragePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin },
    canDeactivate: [(component: ObjectStoragePage) => inject(ObjectStoreCanDeactivateGuard).canDeactivate(component)]
  },
  {
    path: 'external-storage',
    loadComponent: () => import('./pages/external-storage/external-storage.page').then( m => m.ExternalStoragePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'external-storage/:id',
    loadComponent: () => import('./pages/external-storage/external-storage.page').then( m => m.ExternalStoragePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'key-value-database',
    loadComponent: () => import('./pages/key-value-database/key-value-database.page').then( m => m.KeyValueDatabasePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'key-value-database/:id',
    loadComponent: () => import('./pages/key-value-database/key-value-database.page').then( m => m.KeyValueDatabasePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'vector-database',
    loadComponent: () => import('./pages/vector-database/vector-database.page').then( m => m.VectorDatabasePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'vector-database/:id',
    loadComponent: () => import('./pages/vector-database/vector-database.page').then( m => m.VectorDatabasePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'graph-database',
    loadComponent: () => import('./pages/graph-database/graph-database.page').then( m => m.GraphDatabasePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'graph-database/:id',
    loadComponent: () => import('./pages/graph-database/graph-database.page').then( m => m.GraphDatabasePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'media-stream',
    loadComponent: () => import('./pages/media-stream/media-stream.page').then( m => m.MediaStreamPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'cloudlet',
    loadComponent: () => import('./pages/cloudlet/cloudlet.page').then( m => m.CloudletPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'cloudlet/:id',
    loadComponent: () => import('./pages/cloudlet/cloudlet.page').then( m => m.CloudletPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'mistlet',
    loadComponent: () => import('./pages/mistlet/mistlet.page').then( m => m.MistletPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'mistlet/:id',
    loadComponent: () => import('./pages/mistlet/mistlet.page').then( m => m.MistletPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'mistlet/:id/:mistletId',
    loadComponent: () => import('./pages/mistlet/mistlet.page').then( m => m.MistletPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'function',
    loadComponent: () => import('./pages/function/function.page').then( m => m.FunctionPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'virtual-private-mist',
    loadComponent: () => import('./pages/virtual-private-mist/virtual-private-mist.page').then( m => m.VirtualPrivateMistPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'virtual-private-mist/:id',
    loadComponent: () => import('./pages/virtual-private-mist/virtual-private-mist.page').then( m => m.VirtualPrivateMistPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  // Workflow chat (public, no auth)
  {
    path: 'chat/:accountId/:vpmId/:workflowId',
    loadComponent: () => import('./pages/chat/chat.page').then( m => m.ChatPage)
  },
  {
    path: 'chat/:accountId/404',
    loadComponent: () => import('./pages/chat/chat.page').then( m => m.ChatPage)
  },
  {
    path: 'connect',
    loadComponent: () => import('./pages/connect/connect.page').then( m => m.ConnectPage),
    canActivate: authAndRole, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  // Account
  {
    path: 'account',
    loadComponent: () => import('./pages/profile/profile.page').then( m => m.ProfilePage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  // API keys (and authorized domains) for the current Forge account; org admins manage keys here.
  {
    path: 'api-keys',
    loadComponent: () => import('./pages/account/account.page').then( m => m.AccountPage),
    canActivate: aiStudio, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'profile',
    redirectTo: 'account',
    pathMatch: 'full',
  },
  {
    path: 'create-organization',
    loadComponent: () => import('./pages/create-organization/create-organization.page').then(m => m.CreateOrganizationPage),
    canActivate: authAndRole, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'organization',
    loadComponent: () => import('./pages/user-management/user-management.page').then( m => m.UserManagementPage),
    canActivate: authAndRole, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'usage',
    loadComponent: () => import('./pages/usage/usage.page').then(m => m.UsagePage),
    canActivate: authAndRole, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'billing',
    loadComponent: () => import('./pages/billing/billing.page').then(m => m.BillingPage),
    canActivate: authAndRole, data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  // Product-disabled (auth only, no role/product guard)
  {
    path: 'product-disabled',
    loadComponent: () => import('./pages/product-disabled/product-disabled.page').then(m => m.ProductDisabledPage),
    canActivate: [AuthGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  // Public pages
  {
    path: 'organization-invitation',
    loadComponent: () => import('./pages/organization-invitation/organization-invitation.page').then( m => m.OrganizationInvitationPage),
  },
  {
    // Login + signup are centralized in the external auth portal. The `/login`
    // PATH is kept because many places still navigate to it (the
    // redirectUnauthorizedTo(['login']) pipe, account/chat/org-license/
    // user-management pages, role.guard). The route is now a guard-only handoff:
    // AuthGuard + redirectLoggedInToHome hands an unauthenticated hit off to the
    // portal via bridge.login() and redirects an authenticated hit to /workflow,
    // so the stub component below is only shown momentarily before the redirect.
    path: 'login',
    loadComponent: () => import('./pages/auth-handoff/auth-handoff.component').then( m => m.AuthHandoffComponent),
    canActivate: [AuthGuard], data: { authGuardPipe: redirectLoggedInToHome }
  },
  {
    path: 'signing-out',
    loadComponent: () => import('./pages/signing-out/signing-out.page').then( m => m.SigningOutPage)
  },
  {
    path: 'aws-fulfillment',
    loadComponent: () => import('./pages/aws-fulfillment/aws-fulfillment.page').then( m => m.AwsFulfillmentPage)
  },
  {
    path: '',
    redirectTo: 'workflow',
    pathMatch: 'full',
  },
];
